import { useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import { client } from '../../../helpers/api-client';
import { useStrokeResultsSubmission } from '../../../helpers/forth-valley-hooks';
import EyeMotilityResults from './eye-motility-results';
import NeuroAcuityResults from './neuro-acuity-results';
import NeuroVisualFieldResults from './neuro-vf-results';
import { useResultData } from '../../../helpers/useResultData';
import SavingResultsMsg from './SavingResultsMsg';
import { SessionActions, ToggleGroup, useSessionUI } from '../../../ui';
import { StepBody, useConfirmLeave } from '../testScreenKit';

// Stroke Suite results: Eye Motility (Images) | Visual Acuity | Visual Field | Eye Motility (Video).

const NeuroSuiteResults = ({ localResults, setActiveTab, updateStep }) => {
  const [selectedResultsView, setSelectedResultsView] = useState('Eye Motility (Images)');
  const [resultsFromServer, setResultsFromServer] = useState([]);
  const [eyeMotilityImages, setEyeMotilityImages] = useState([]);
  const [eyeMotilityVideo, setEyeMotilityVideo] = useState(null);
  const [usingOldImages, setUsingOldImages] = useState(false);
  const [savingResults, setSavingResults] = useState(false);
  const [skippedTest, setSkippedTest] = useState(false);
  const { testData } = useTestContext();
  const { clearAllResults } = useResultData();
  const submitResults = useStrokeResultsSubmission();
  const { confirm } = useSessionUI();
  const confirmLeave = useConfirmLeave();

  useEffect(() => {
    const getRemoteTestResults = () => {
      const testId = testData.testResultsId;
      client(`api/appointment-test-result/${testId}`).then(
        (data) => {
          if (!data?.result_data) return;
          const allResults = JSON.parse(data.result_data);
          const oldImages = allResults.visualFieldForthValley.results.motilityImages;
          if (oldImages) setUsingOldImages(true);
          const mediaFiles = data.media;
          const images = oldImages || mediaFiles.filter((file) => file.type.includes('image'));
          const video = mediaFiles?.find((file) => file.type.includes('video'));
          setEyeMotilityImages({ images: images });
          setEyeMotilityVideo({ video: video });
          setResultsFromServer(allResults);
          if (!allResults?.visualFieldForthValley?.results?.forthValleyVisualFieldDots?.length) setSkippedTest(true);
        },
        (error) => {
          console.error(error);
        },
      );
    };

    if (!localResults) return getRemoteTestResults();
    if (!localResults.forthValleyVisualFieldDots?.length) return setSkippedTest(true);
  }, [localResults, testData.testResultsId]);

  const renderSwitch = () => {
    switch (selectedResultsView) {
      case 'Eye Motility (Images)':
        return <EyeMotilityResults showImages testData={eyeMotilityImages} usingOldImages={usingOldImages} />;
      case 'Visual Acuity':
        return <NeuroAcuityResults testData={localResults || resultsFromServer} />;
      case 'Visual Field':
        return <NeuroVisualFieldResults testData={localResults || resultsFromServer} />;
      case 'Eye Motility (Video)':
        return <EyeMotilityResults showVideo testData={eyeMotilityVideo} />;
      case 'SavingResults':
        return <SavingResultsMsg />;
      default:
        return <EyeMotilityResults showImages testData={eyeMotilityImages} usingOldImages={usingOldImages} />;
    }
  };

  const saveTestResults = async () => {
    if (await confirm({ message: 'Are you sure you want to save the test results?' })) {
      setSavingResults(true);
      setSelectedResultsView('SavingResults');
      const response = await submitResults();
      if (response) {
        setActiveTab('results');
      }
    }
  };

  const restartTest = () => {
    confirmLeave('restart').then(() => {
      clearAllResults();
      return updateStep('eyeMotility');
    });
  };

  const views = ['Eye Motility (Images)', ...(!skippedTest ? ['Visual Acuity', 'Visual Field', 'Eye Motility (Video)'] : ['Eye Motility (Video)'])];

  return (
    <StepBody gap={16} data-testid='neuro-results'>
      {!savingResults && (
        <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
          <ToggleGroup ariaLabel='Results view' options={views} value={selectedResultsView} onChange={setSelectedResultsView} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 0, overflow: 'auto', width: '100%' }}>{renderSwitch()}</div>
      <SessionActions
        onBack={!savingResults ? () => (localResults ? restartTest() : setActiveTab('results')) : undefined}
        backLabel={localResults ? 'Restart' : 'Back'}
        primary={localResults ? { label: savingResults ? 'Saving…' : 'Save Test Results', onClick: () => saveTestResults(), disabled: savingResults } : undefined}
      />
    </StepBody>
  );
};

export default NeuroSuiteResults;
